/**
 * Enhanced API client for all iBest applications.
 * Features: retry logic, request cancellation, caching, deduplication.
 */

import { config } from '../config';

export class ApiError extends Error {
  status: number;
  response?: unknown;

  constructor(message: string, status: number, response?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.response = response;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  params?: Record<string, string>;
  timeout?: number; // Request timeout in ms
  retry?: number; // Number of retry attempts
  retryDelay?: number; // Delay between retries in ms
  useCache?: boolean; // Enable response caching
  deduplicate?: boolean; // Deduplicate concurrent requests
}

// Response cache
const responseCache = new Map<string, { data: unknown; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// In-flight requests for deduplication
const inflightRequests = new Map<string, Promise<unknown>>();

function notifyAuthExpired(): void {
  window.dispatchEvent(
    new CustomEvent('iBestAuthExpired', {
      detail: { message: 'Your session expired. Please connect your wallet again.' }
    })
  );
}

/**
 * Generate cache key from endpoint and options
 */
function getCacheKey(endpoint: string, options: RequestOptions): string {
  const paramsStr = options.params ? JSON.stringify(options.params) : '';
  const bodyStr = options.body ? JSON.stringify(options.body) : '';
  return `${options.method || 'GET'}:${endpoint}:${paramsStr}:${bodyStr}`;
}

/**
 * Sleep utility for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Enhanced API request with retry, timeout, caching, and deduplication
 */
export async function apiRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const {
    body,
    params,
    headers,
    timeout = config.api.timeout,
    retry = config.features.enableRetry ? 2 : 0, // Use feature flag!
    retryDelay = 1000,
    useCache = false,
    deduplicate = true,
    signal,
    ...fetchOptions
  } = options;

  const cacheKey = getCacheKey(endpoint, options);

  // Check cache first
  if (useCache && options.method === 'GET') {
    const cached = responseCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data as T;
    }
  }

  // Deduplicate concurrent requests
  if (deduplicate) {
    const inflight = inflightRequests.get(cacheKey);
    if (inflight) {
      return inflight as Promise<T>;
    }
  }

  // Build URL with query params
  const url = new URL(`${config.api.baseUrl}${endpoint}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
  }

  // Auto-stringify body if it's an object
  const finalBody = body ? JSON.stringify(body) : undefined;

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Combine user signal with timeout signal
  if (signal) {
    signal.addEventListener('abort', () => controller.abort());
  }

  const executeRequest = async (): Promise<T> => {
    try {
      const res = await fetch(url.toString(), {
        ...fetchOptions,
        headers: {
          'Content-Type': 'application/json',
          ...headers
        },
        body: finalBody,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!res.ok) {
        const text = await res.text();
        if (res.status === 401) {
          notifyAuthExpired();
        }
        throw new ApiError(text || res.statusText, res.status);
      }

      const data = await res.json();

      // Cache successful GET requests
      if (useCache && options.method === 'GET') {
        responseCache.set(cacheKey, { data, timestamp: Date.now() });
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  // Retry logic with exponential backoff
  const requestWithRetry = async (): Promise<T> => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retry; attempt++) {
      try {
        return await executeRequest();
      } catch (error) {
        lastError = error as Error;

        // Don't retry on abort, 4xx errors, or last attempt
        if ((error instanceof DOMException && error.name === 'AbortError') || (error instanceof ApiError && error.status >= 400 && error.status < 500) || attempt === retry) {
          throw error;
        }

        // Exponential backoff: 1s, 2s, 4s, 8s...
        const delay = retryDelay * Math.pow(2, attempt);
        console.warn(`Request failed (attempt ${attempt + 1}/${retry + 1}), retrying in ${delay}ms...`);
        await sleep(delay);
      }
    }

    throw lastError;
  };

  // Store in-flight request for deduplication
  const requestPromise = requestWithRetry().finally(() => {
    inflightRequests.delete(cacheKey);
  });

  if (deduplicate) {
    inflightRequests.set(cacheKey, requestPromise);
  }

  return requestPromise;
}

/**
 * POST request helper
 */
export async function apiPost<T>(endpoint: string, body: unknown, options?: Omit<RequestOptions, 'body' | 'method'>): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'POST', body, ...options });
}

/**
 * GET request helper with caching
 */
export async function apiGet<T>(endpoint: string, params?: Record<string, string>, options?: Omit<RequestOptions, 'params' | 'method'>): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'GET', params, useCache: true, ...options });
}

/**
 * Clear response cache (useful for logout or data refresh)
 */
export function clearApiCache(pattern?: string): void {
  if (!pattern) {
    responseCache.clear();
    return;
  }

  // Clear cache entries matching pattern
  for (const key of responseCache.keys()) {
    if (key.includes(pattern)) {
      responseCache.delete(key);
    }
  }
}

/**
 * Create a cancellable request
 * Usage:
 * const { request, cancel } = cancellableRequest('/endpoint', { ... });
 * request.then(data => ...).catch(err => ...);
 * // Later: cancel();
 */
export function cancellableRequest<T>(endpoint: string, options: RequestOptions = {}): { request: Promise<T>; cancel: () => void } {
  const controller = new AbortController();

  const request = apiRequest<T>(endpoint, {
    ...options,
    signal: controller.signal
  });

  return {
    request,
    cancel: () => controller.abort()
  };
}
